/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import type { MapRef } from 'react-map-gl/mapbox'

import { SavedViewPopover } from '@/components/ui/saved-views/SavedViewPopover'
import { useSettledDirty } from '@/components/ui/saved-views/useSettledDirty'

import { useMapControls } from './hooks/useMapControls'
import { useMapContext, type SaveableAssetMapContext } from './MapContext'
import { useStatusColumnsContext } from './StatusColumns'

type Snapshot = Pick<SaveableAssetMapContext, 'companyId' | 'filters' | 'sidebarState' | 'mapState' | 'columns'>

/**
 * What a saved view is compared on. The viewport, pane sizes and column widths are left out, so
 * panning the map or dragging a divider doesn't count as a change; saving still stores them.
 */
export const comparable = ({ companyId, filters, sidebarState, mapState, columns }: Snapshot) => ({
  companyId: companyId || 0,
  filters: {
    ...filters,
    companyId: undefined,
    alarmCodes: Array.from(filters.alarmCodes).sort(),
    alerts: Array.from(filters.alerts).sort(),
  },
  layoutMode: sidebarState.layoutMode,
  limitToMap: sidebarState.limitToMap,
  viewMode: sidebarState.viewMode,
  style: mapState.style,
  clustersEnabled: mapState.clustersEnabled,
  assetLabels: mapState.assetLabels,
  severitySource: mapState.severitySource,
  showQuickView: mapState.showQuickView,
  zOrders: mapState.zOrders,
  geofenceCategoryIds: mapState.geofenceCategoryIds,
  columns: (columns ?? [])
    .map((c) => ({ name: c.selectedColumnsDatabaseName, selected: c.selected, order: c.displayOrder }))
    .sort((a, b) => a.name.localeCompare(b.name)),
})

/** The asset map's saved views (kept in the profile's assetMaps), in the shared popover. */
export const AssetSavedViews = ({ mapRef }: { mapRef: React.RefObject<MapRef | null> }) => {
  const { selectedSavedMap, filters, sidebarState, mapState, isSwitchingView } = useMapContext()
  const { statusColumns, isLoadingColumns } = useStatusColumnsContext()
  const controls = useMapControls(mapRef)

  const isDirty = useSettledDirty(
    selectedSavedMap,
    comparable({ companyId: filters.companyId || 0, filters, sidebarState, mapState, columns: statusColumns }),
    isSwitchingView || isLoadingColumns
  )

  return (
    <SavedViewPopover
      selected={selectedSavedMap}
      viewNames={controls.savedMapOptions.map((o) => o.value)}
      isDirty={isDirty}
      onSaveAsNew={controls.saveAsNewMap}
      onSaveSelected={controls.saveSelectedMap}
      onSetDefault={controls.setSelectedMapIsDefault}
      onDelete={controls.deleteSelectedMap}
      onSelect={(name) => controls.handleSavedMapChange({ value: name, label: name })}
      onReset={controls.handleReset}
      resetHelp="Clears the search and all filters, puts the layout and map settings back to how they start, and zooms out. Keeps the company, your columns and your saved views."
    />
  )
}
